import { z } from 'zod';
import { CardType } from '../../../samba-payment.types';
import {
    CloverGatewayTransactionDetailsType,
    type CloverNormalizedGatewayTransactionDetails,
    type CloverNormalizedGatewayTransactionDetailsV1,
    type CloverNormalizedGatewayTransactionDetailsV2,
    type CloverNormalizedAdditionalCharge,
    type CloverTransactionDetailsAdditionalChargeBody,
    type CloverTransactionDetailsAdditionalChargesBody,
    type CloverTransactionDetailsCardTransactionBody,
    CloverTransactionDetailsOutcome,
    type CloverTransactionDetailsPaymentBody,
    CloverTransactionDetailsPaymentResult,
    type CloverTransactionDetailsRefundBody,
    type CloverTransactionDetailsRequestDetails,
    type CloverTransactionDetailsRequestHeaders,
    type CloverPlatformTransactionDetailsRequestHeaders,
    type CloverTransactionDetailsResponseDetails,
    type CloverTransactionStatusPaymentResponse,
    type CloverTransactionDetailsTenderBody,
    type CloverTransactionDetailsRefundsBody,
} from '../../../types/ipg/clover/clover-transaction-details.types';
import { CloverGatewayId } from '../../../types/ipg/clover/clover-system-constants';
import {
    StoredJsonNumberRecordSchema,
    defineSafeIntegerDomain,
    normalizeStoredJsonSafeIntegerObjectFields,
} from '../../../stored-json-number';
import { omitEmptyCloverCardTransactionText } from './clover-card-transaction.schemas';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
} from '../../../stored-json-contract.types';
import { JsonNonnegativeLosslessNumberInputSchema } from '../../common.schemas';

const CloverStoredTransactionDetailsAmountFields: readonly string[] = [
    'amount',
    'taxAmount',
    'tipAmount',
];

const CloverStoredTransactionDetailsTimestampFields: readonly string[] = ['createdTime'];

const CloverTransactionDetailsAmountSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'clover-transaction-details.amount',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'CloverTransactionDetailsAmountSchema',
        minimum: 0,
        maximum: 999999999,
    },
});

const CloverTransactionDetailsTimestampSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'clover-transaction-details.timestamp',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'CloverTransactionDetailsTimestampSchema',
        minimum: 0,
        maximum: Number.MAX_SAFE_INTEGER,
    },
});

export const CloverGatewayTransactionCardTypeSchema: z.ZodType<CardType> = z.enum(CardType);

export const CloverTransactionDetailsPaymentResultSchema: z.ZodType<
    (typeof CloverTransactionDetailsPaymentResult)[keyof typeof CloverTransactionDetailsPaymentResult]
> = z.enum(CloverTransactionDetailsPaymentResult);

export const CloverTransactionDetailsAmountSchema: z.ZodNumber = z
    .number()
    .int()
    .min(0)
    .max(999999999);

export const CloverTransactionDetailsTimestampSchema: z.ZodNumber = z
    .number()
    .int()
    .min(0)
    .max(Number.MAX_SAFE_INTEGER);

export const CloverTransactionDetailsRequestHeadersSchema: z.ZodObject<{
    deviceId: z.ZodString;
    posId: z.ZodString;
    userAgent: z.ZodString;
    timeoutSeconds: z.ZodOptional<z.ZodNumber>;
}> = z.object({
    deviceId: z.string().min(1),
    posId: z.string().min(1),
    userAgent: z.string().min(1),
    timeoutSeconds: z.number().int().min(0).max(300).optional(),
}) satisfies z.ZodType<CloverTransactionDetailsRequestHeaders>;

export const CloverPlatformTransactionDetailsRequestHeadersSchema: z.ZodObject<{
    userAgent: z.ZodString;
}> = z.object({
    userAgent: z.string().min(1),
}) satisfies z.ZodType<CloverPlatformTransactionDetailsRequestHeaders>;

export const CloverTransactionDetailsRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'GET'>;
    path: z.ZodLiteral<'/v3/merchants/{merchantId}/payments/{paymentId}'>;
    merchantId: z.ZodString;
    paymentId: z.ZodString;
    expand: z.ZodLiteral<'additionalCharges,refunds,cardTransaction,tender'>;
    headers: typeof CloverPlatformTransactionDetailsRequestHeadersSchema;
}> = z.object({
    method: z.literal('GET'),
    path: z.literal('/v3/merchants/{merchantId}/payments/{paymentId}'),
    merchantId: z.string().min(1),
    paymentId: z.string().min(1),
    expand: z.literal('additionalCharges,refunds,cardTransaction,tender'),
    headers: CloverPlatformTransactionDetailsRequestHeadersSchema,
}) satisfies z.ZodType<CloverTransactionDetailsRequestDetails>;

export const CloverTransactionDetailsCardTransactionBodyFieldsSchema: z.ZodObject<{
    authCode: z.ZodOptional<z.ZodString>;
    cardType: z.ZodOptional<z.ZodString>;
    cardholderName: z.ZodOptional<z.ZodString>;
    last4: z.ZodOptional<z.ZodString>;
    referenceId: z.ZodOptional<z.ZodString>;
    state: z.ZodOptional<z.ZodString>;
    token: z.ZodOptional<z.ZodString>;
    transactionNo: z.ZodOptional<z.ZodString>;
    type: z.ZodOptional<z.ZodString>;
}> = z.object({
    authCode: z.string().min(1).optional(),
    cardType: z.string().min(1).optional(),
    cardholderName: z.string().min(1).optional(),
    last4: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
    referenceId: z.string().min(1).optional(),
    state: z.string().min(1).optional(),
    token: z.string().min(1).optional(),
    transactionNo: z.string().min(1).optional(),
    type: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverTransactionDetailsCardTransactionBody>;

export const CloverTransactionDetailsCardTransactionBodySchema: z.ZodType<CloverTransactionDetailsCardTransactionBody> =
    z.preprocess(
        omitEmptyCloverCardTransactionText,
        CloverTransactionDetailsCardTransactionBodyFieldsSchema
    );

export const CloverTransactionDetailsTenderBodySchema: z.ZodObject<{
    id: z.ZodOptional<z.ZodString>;
    label: z.ZodOptional<z.ZodString>;
    labelKey: z.ZodOptional<z.ZodString>;
}> = z.object({
    id: z.string().min(1).optional(),
    label: z.string().min(1).optional(),
    labelKey: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverTransactionDetailsTenderBody>;

export const CloverTransactionDetailsRefundBodySchema: z.ZodType<CloverTransactionDetailsRefundBody> =
    z.object({
        id: z.string().min(1).optional(),
        amount: CloverTransactionDetailsAmountSchema.optional(),
        createdTime: CloverTransactionDetailsTimestampSchema.optional(),
        externalReferenceId: z.string().min(1).max(12).optional(),
        payment: z
            .object({
                id: z.string().min(1).optional(),
            })
            .optional(),
        taxAmount: CloverTransactionDetailsAmountSchema.optional(),
        tipAmount: CloverTransactionDetailsAmountSchema.optional(),
        voided: z.boolean().optional(),
    });

export const CloverTransactionDetailsAdditionalChargeBodySchema: z.ZodType<CloverTransactionDetailsAdditionalChargeBody> =
    z
        .object({
            id: z.string().min(1).optional(),
            amount: CloverTransactionDetailsAmountSchema,
            rate: CloverTransactionDetailsAmountSchema.optional(),
            type: z.string().min(1),
            payment: z
                .object({ id: z.string().min(1) })
                .strict()
                .optional(),
            refund: z
                .object({ id: z.string().min(1) })
                .strict()
                .optional(),
        })
        .strict();

export const CloverTransactionDetailsAdditionalChargesBodySchema: z.ZodType<CloverTransactionDetailsAdditionalChargesBody> =
    z
        .object({
            elements: z.array(CloverTransactionDetailsAdditionalChargeBodySchema),
        })
        .strict();

export const CloverTransactionDetailsRefundsBodySchema: z.ZodType<CloverTransactionDetailsRefundsBody> =
    z
        .object({
            elements: z.array(CloverTransactionDetailsRefundBodySchema),
        })
        .strict();

export const CloverTransactionDetailsPaymentBodySchema: z.ZodType<CloverTransactionDetailsPaymentBody> =
    z.object({
        id: z.string().min(1),
        amount: CloverTransactionDetailsAmountSchema.optional(),
        result: CloverTransactionDetailsPaymentResultSchema.optional(),
        cardTransaction: CloverTransactionDetailsCardTransactionBodySchema.optional(),
        createdTime: CloverTransactionDetailsTimestampSchema.optional(),
        externalPaymentId: z.string().min(1).max(32).optional(),
        externalReferenceId: z.string().min(1).max(12).optional(),
        offline: z.boolean().optional(),
        refunds: z
            .union([
                z.array(CloverTransactionDetailsRefundBodySchema),
                CloverTransactionDetailsRefundsBodySchema,
            ])
            .optional(),
        additionalCharges: CloverTransactionDetailsAdditionalChargesBodySchema.optional(),
        taxAmount: CloverTransactionDetailsAmountSchema.optional(),
        tipAmount: CloverTransactionDetailsAmountSchema.optional(),
        tender: CloverTransactionDetailsTenderBodySchema.optional(),
        voided: z.boolean().optional(),
    });

export const CloverTransactionStatusPaymentResponseSchema: z.ZodObject<{
    payment: typeof CloverTransactionDetailsPaymentBodySchema;
}> = z.object({
    payment: CloverTransactionDetailsPaymentBodySchema,
}) satisfies z.ZodType<CloverTransactionStatusPaymentResponse>;

function normalizeStoredCloverTransactionDetailsNumericFields(value: unknown): unknown {
    const rootResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!rootResult.success) {
        return value;
    }

    const amountNormalizedRoot = normalizeStoredJsonSafeIntegerObjectFields(rootResult.data, {
        safeIntegerDomain: CloverTransactionDetailsAmountSafeIntegerDomain,
        fields: CloverStoredTransactionDetailsAmountFields,
    });
    const timestampNormalizedRoot = normalizeStoredJsonSafeIntegerObjectFields(
        amountNormalizedRoot,
        {
            safeIntegerDomain: CloverTransactionDetailsTimestampSafeIntegerDomain,
            fields: CloverStoredTransactionDetailsTimestampFields,
        }
    );
    const normalizedRootResult = StoredJsonNumberRecordSchema.safeParse(timestampNormalizedRoot);
    if (!normalizedRootResult.success) {
        return value;
    }

    const normalizeRefund = (refund: unknown): unknown => {
        const amountNormalizedRefund = normalizeStoredJsonSafeIntegerObjectFields(refund, {
            safeIntegerDomain: CloverTransactionDetailsAmountSafeIntegerDomain,
            fields: CloverStoredTransactionDetailsAmountFields,
        });
        return normalizeStoredJsonSafeIntegerObjectFields(amountNormalizedRefund, {
            safeIntegerDomain: CloverTransactionDetailsTimestampSafeIntegerDomain,
            fields: CloverStoredTransactionDetailsTimestampFields,
        });
    };
    const refunds = normalizedRootResult.data.refunds;
    const refundsRecordResult = StoredJsonNumberRecordSchema.safeParse(refunds);
    const normalizedRefunds = Array.isArray(refunds)
        ? refunds.map(normalizeRefund)
        : refundsRecordResult.success && Array.isArray(refundsRecordResult.data.elements)
          ? {
                ...refundsRecordResult.data,
                elements: refundsRecordResult.data.elements.map(normalizeRefund),
            }
          : refunds;
    const additionalChargesResult = StoredJsonNumberRecordSchema.safeParse(
        normalizedRootResult.data.additionalCharges
    );
    const normalizedAdditionalCharges =
        additionalChargesResult.success && Array.isArray(additionalChargesResult.data.elements)
            ? {
                  ...additionalChargesResult.data,
                  elements: additionalChargesResult.data.elements.map((additionalCharge) =>
                      normalizeStoredJsonSafeIntegerObjectFields(additionalCharge, {
                          safeIntegerDomain: CloverTransactionDetailsAmountSafeIntegerDomain,
                          fields: ['amount', 'rate'],
                      })
                  ),
              }
            : normalizedRootResult.data.additionalCharges;

    return {
        ...normalizedRootResult.data,
        ...(refunds === undefined ? {} : { refunds: normalizedRefunds }),
        ...(normalizedRootResult.data.additionalCharges === undefined
            ? {}
            : { additionalCharges: normalizedAdditionalCharges }),
    };
}

export const StoredCloverTransactionDetailsPaymentBodySchema: z.ZodType<CloverTransactionDetailsPaymentBody> =
    z.preprocess(
        normalizeStoredCloverTransactionDetailsNumericFields,
        CloverTransactionDetailsPaymentBodySchema
    );

const CloverNormalizedGatewayTransactionDetailsSchemaBase = z.object({
    gateway_id: z.literal(CloverGatewayId),
    gateway_transaction_details_type: z.literal(CloverGatewayTransactionDetailsType),
    gateway_txn_id: z.string().min(1),
    base_amount: JsonNonnegativeLosslessNumberInputSchema,
    total_amount: JsonNonnegativeLosslessNumberInputSchema,
    total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
    total_tax_amount: JsonNonnegativeLosslessNumberInputSchema,
    is_void: z.boolean(),
    is_settled: z.boolean(),
    refund_amount: JsonNonnegativeLosslessNumberInputSchema,
    refund_count: z.number().int().nonnegative(),
    card_brand: z.string().min(1).optional(),
    card_last4: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
    cardholder_name: z.string().min(1).optional(),
    card_type: CloverGatewayTransactionCardTypeSchema.optional(),
    settlement_status: z.string().min(1).optional(),
    authorization_code: z.string().min(1).optional(),
    batch_id: z.string().min(1).optional(),
});

export const CloverNormalizedAdditionalChargeSchema: z.ZodType<CloverNormalizedAdditionalCharge> = z
    .object({
        provider_id: z.string().min(1).optional(),
        amount: JsonNonnegativeLosslessNumberInputSchema,
        rate: JsonNonnegativeLosslessNumberInputSchema.optional(),
        type: z.string().min(1),
        payment_id: z.string().min(1).optional(),
        refund_id: z.string().min(1).optional(),
    })
    .strict();

export const CloverNormalizedGatewayTransactionDetailsV1Schema: z.ZodType<CloverNormalizedGatewayTransactionDetailsV1> =
    CloverNormalizedGatewayTransactionDetailsSchemaBase.extend({
        gateway_transaction_details_version: z.literal(1),
    });

export const CloverNormalizedGatewayTransactionDetailsV2Schema: z.ZodType<CloverNormalizedGatewayTransactionDetailsV2> =
    CloverNormalizedGatewayTransactionDetailsSchemaBase.extend({
        gateway_transaction_details_version: z.literal(2),
        credit_surcharge_amount: JsonNonnegativeLosslessNumberInputSchema,
        additional_charges: z.array(CloverNormalizedAdditionalChargeSchema),
    }).strict();

export const CloverNormalizedGatewayTransactionDetailsStorageContract = defineStoredJsonContract({
    contractName: 'CloverNormalizedGatewayTransactionDetails',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'CloverNormalizedGatewayTransactionDetailsV1Schema',
            schema: CloverNormalizedGatewayTransactionDetailsV1Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['gateway_transaction_details_version'],
                    markerValue: 1,
                },
            ],
            normalize: (
                value: CloverNormalizedGatewayTransactionDetailsV1
            ): CloverNormalizedGatewayTransactionDetails => ({
                ...value,
                gateway_transaction_details_version: 2,
                credit_surcharge_amount: null,
                additional_charges: null,
            }),
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'CloverNormalizedGatewayTransactionDetailsV2Schema',
            schema: CloverNormalizedGatewayTransactionDetailsV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['gateway_transaction_details_version'],
                    markerValue: 2,
                },
            ],
            normalize: (
                value: CloverNormalizedGatewayTransactionDetailsV2
            ): CloverNormalizedGatewayTransactionDetails => value,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const CloverNormalizedGatewayTransactionDetailsSchema: z.ZodType<CloverNormalizedGatewayTransactionDetails> =
    buildStoredJsonValueParser(CloverNormalizedGatewayTransactionDetailsStorageContract);

export const CloverTransactionDetailsResponseDetailsSchema: z.ZodType<CloverTransactionDetailsResponseDetails> =
    z.discriminatedUnion('outcome', [
        z.object({
            outcome: z.literal(CloverTransactionDetailsOutcome.FOUND),
            httpStatus: z.literal(200),
            rawDetails: CloverTransactionDetailsPaymentBodySchema,
            normalizedDetails: CloverNormalizedGatewayTransactionDetailsSchema,
        }),
        z.object({
            outcome: z.literal(CloverTransactionDetailsOutcome.MISSING),
            httpStatus: z.number().int().positive(),
            rawDetails: z.unknown(),
            message: z.string().min(1),
        }),
        z.object({
            outcome: z.literal(CloverTransactionDetailsOutcome.MALFORMED_RESPONSE),
            httpStatus: z.number().int().positive(),
            rawDetails: z.unknown(),
            message: z.string().min(1),
        }),
    ]);
